import {
  Pulse_Img,
  Secure_llm_img,
} from "../../assets";
import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { MOTION_OK, revealTrigger, riseFrom } from "../../utils/animations";

export default function ValueProposition() {
  const sectionRef = useRef(null);
  const glowRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        riseFrom(".vp-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".vp-head"),
        });
        riseFrom(".vp-card", {
          y: 48,
          duration: 1,
          stagger: 0.2,
          scrollTrigger: revealTrigger(".vp-grid"),
        });
      });

      // Cursor-following glow, scoped to this section only. The gradient is
      // centered on its home position (left 50% / top 70%) via percentage
      // transforms, and `x`/`y` add a pixel offset that eases toward the
      // pointer. It returns home when the cursor leaves or the section
      // scrolls out of view.
      mm.add(
        { motionOk: MOTION_OK, isDesktop: "(min-width: 768px)" },
        (ctx) => {
          const section = sectionRef.current;
          const glow = glowRef.current;
          if (!section || !glow) return;

          const { isDesktop } = ctx.conditions;
          const FOLLOW = 0.7; // how tightly the glow tracks the pointer

          gsap.set(glow, {
            xPercent: -50,
            yPercent: isDesktop ? -30 : 10,
          });

          const moveX = gsap.quickTo(glow, "x", {
            duration: 0.7,
            ease: "power3.out",
          });
          const moveY = gsap.quickTo(glow, "y", {
            duration: 0.7,
            ease: "power3.out",
          });

          const onMove = (e) => {
            const rect = section.getBoundingClientRect();
            const homeX = rect.width * 0.5;
            const homeY = rect.height * 0.7;
            moveX((e.clientX - rect.left - homeX) * FOLLOW);
            moveY((e.clientY - rect.top - homeY) * FOLLOW);
          };

          const reset = () => {
            moveX(0);
            moveY(0);
          };

          section.addEventListener("mousemove", onMove);
          section.addEventListener("mouseleave", reset);

          // Snap back to the default position once scrolled past the section.
          const st = ScrollTrigger.create({
            trigger: section,
            start: "top bottom",
            end: "bottom top",
            onLeave: reset,
            onLeaveBack: reset,
          });

          return () => {
            section.removeEventListener("mousemove", onMove);
            section.removeEventListener("mouseleave", reset);
            st.kill();
          };
        }
      );

      return () => mm.revert();
    },
    { scope: sectionRef }
  );

  return (
    <section ref={sectionRef} className="bg-[#010127] relative pb-[96px]">
      <div
        ref={glowRef}
        className="w-[150px] lg:w-[400px] h-[200px] bg-[#0073FF]/40 blur-[120px] absolute top-[70%] left-[50%] translate-x-[-50%] translate-y-[10%] md:translate-y-[-30%] pointer-events-none will-change-transform"
      ></div>
      <div className="container flex flex-col gap-[48px] z-50">
        <div className="vp-head space-y-2">
          <h2 className="text-white font-extrabold text-2xl md:text-3xl lg:text-[32px] text-center">
            Two disciplines. One platform
          </h2>

          <p className="text-[#C0C0C0] text-sm md:text-base text-center">
            Secure AI-powered platforms and assistants built for precision,
            privacy, and scale.
          </p>
        </div>

        <div className="vp-grid grid grid-cols-12 md:grid-rows-2 gap-8 z-50">
          <div className="vp-card col-span-12 md:col-span-6 md:row-span-2 bg-[#FFFFFF0D] flex flex-col items-center p-6 gap-4 rounded-xl">
            <div>
              <h4 className="text-white text-lg md:text-xl lg:text-2xl font-bold">
                Analytics
              </h4>
              <p className="text-[#88BEFF] text-base md:text-lg font-bold">
                Managed compute infrastructure. Built for analytics scale.
              </p>
              <p className="text-[#C0C0C0] text-sm md:text-base">
                Purpose-built Analytics Processing Units accelerate Apache Spark
                workloads at silicon speed, delivering up to 100× faster
                performance and up to 80% lower query costs for petabyte-scale
                analytics, with enterprise cloud or on-premises deployment for
                full data sovereignty.
              </p>
            </div>
            <div className="flex items-center justify-center w-full h-full">
              <img
                className="w-full md:max-w-[250px]"
                src={Pulse_Img}
                alt="value-diagrams"
              />
            </div>
          </div>
          <div className="vp-card col-span-12 md:col-span-6 md:row-span-2 bg-[#FFFFFF0D] flex flex-col items-center p-6 gap-4 rounded-xl">
            <div>
              <h4 className="text-white text-lg md:text-xl lg:text-2xl font-bold">
                Intelligence
              </h4>
              <p className="text-[#88BEFF] text-base md:text-lg font-bold">
                An analytics reasoning model. Built for the agentic era.
              </p>
              <p className="text-[#C0C0C0] text-sm md:text-base">
                A domain-specialised reasoning model built for analytical
                intelligence, autonomous AI agents, and auditable insight
                generation, delivering verifiable chain-of-thought reasoning for
                enterprise governance and trust.
              </p>
            </div>
            <div className="flex items-center justify-center w-full h-full">
              <img
                className="w-full md:max-w-[390px]"
                src={Secure_llm_img}
                alt="value-diagrams"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
